/**
 * Room API Service
 *
 * Client-side API functions for room management.
 * Handles HTTP requests to backend room endpoints.
 */

import { post, get, del } from '@/lib/api-client';
import type {
  RoomCreateResponse,
  RoomJoinResponse,
  RoomStateResponse,
  MatchmakingResponse,
  RoomGameType,
  UpdatePresenceResponse,
  WaitingCountsResponse
} from 'shared-types';

/**
 * Creates a new private room
 *
 * @param gameType - The type of game ('free' or 'paid')
 * @returns Promise with created room details
 * @throws Error if request fails
 */
export async function createRoom(gameType: RoomGameType): Promise<RoomCreateResponse> {
  return post<RoomCreateResponse>('/rooms', { gameType });
}

/**
 * Joins an existing room by invitation code
 *
 * @param code - The 6-character room code
 * @returns Promise with joined room details
 * @throws Error if request fails (invalid code, full room, etc.)
 */
export async function joinRoom(code: string): Promise<RoomJoinResponse> {
  return post<RoomJoinResponse>('/rooms/join', { code });
}

/**
 * Gets current state of a room
 *
 * @param code - The 6-character room code
 * @returns Promise with room state and presence members
 * @throws Error if request fails
 */
export async function getRoomState(code: string): Promise<RoomStateResponse> {
  return get<RoomStateResponse>(`/rooms/${code}`);
}

/**
 * Updates room player count based on presence changes
 *
 * @param code - The 6-character room code
 * @param count - Current number of players in the room
 * @returns Promise with updated room data (includes matchmaking timer state)
 * @throws Error if request fails
 */
export async function updatePresence(code: string, count: number): Promise<UpdatePresenceResponse> {
  return post<UpdatePresenceResponse>(`/rooms/${code}/presence`, { count });
}

/**
 * Starts the game for all players in the room (creator only)
 *
 * @param code - The 6-character room code
 * @returns Promise that resolves when game starts
 * @throws Error if request fails
 */
export async function startGame(code: string): Promise<void> {
  await post(`/rooms/${code}/start`, {});
}

/**
 * Closes a room (creator only)
 *
 * @param code - The 6-character room code
 * @returns Promise that resolves when room is closed
 * @throws Error if request fails
 */
export async function closeRoom(code: string): Promise<void> {
  await del(`/rooms/${code}`);
}

/**
 * Starts matchmaking for a game type
 * Finds an existing waiting room or creates a new one
 *
 * @param gameType - The type of game ('free' or 'paid')
 * @returns Promise with room details and isCreator flag
 * @throws Error if request fails
 */
export async function startMatchmaking(gameType: RoomGameType): Promise<MatchmakingResponse> {
  return post<MatchmakingResponse>('/rooms/matchmaking', { gameType });
}

/**
 * Gets count of waiting players in matchmaking rooms by game type
 *
 * @returns Promise with free and paid player counts
 * @throws Error if request fails
 */
export async function getWaitingCounts(): Promise<WaitingCountsResponse> {
  return get<WaitingCountsResponse>('/rooms/waiting-counts');
}

/**
 * Check matchmaking timeout response
 */
export interface CheckMatchmakingTimeoutResponse {
  shouldStart: boolean;
  shouldRefresh: boolean;
  playerCount: number;
}

/**
 * Checks if matchmaking timer has expired and determines next action
 *
 * @param code - The 6-character room code
 * @returns Promise with shouldStart, shouldRefresh, and playerCount
 * @throws Error if request fails
 */
export async function checkMatchmakingTimeout(
  code: string
): Promise<CheckMatchmakingTimeoutResponse> {
  return post<CheckMatchmakingTimeoutResponse>(`/rooms/${code}/check-matchmaking-timeout`, {});
}
